import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const EQUIPMENT_TYPES = ['laptop', 'phone', 'access_card', 'credit_card', 'key', 'other'];
export const STATUSES = ['pending', 'returned', 'confirmed', 'waived'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOdEquipmentApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-equipment?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const markReturned = useCallback(async (id, method, conditionNote, cargoCode) => {
    try {
      await api.post(`/v1/offboarding-equipment/${id}/mark-returned`, {
        returnMethod: method, cargoTrackingCode: cargoCode, conditionNote,
      });
      refresh(); return true;
    } catch { return false; }
  }, [refresh]);

  const confirm = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-equipment/${id}/confirm`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const waive = useCallback(async (id, reason) => {
    try { await api.post(`/v1/offboarding-equipment/${id}/waive`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const typeLabel = (s) => t(`offboardingEquipment.type.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`offboardingEquipment.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'offboardingId', label: 'OD', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingEquipment.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'displayName',
        label: t('offboardingEquipment.displayName'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'equipmentType',
        label: t('offboardingEquipment.typeLabel'),
        options: {
          ...multiSelectFilter(t('offboardingEquipment.typeLabel'), EQUIPMENT_TYPES, typeLabel),
          customBodyRender: (v) => typeLabel(v),
        },
      },
      {
        name: 'serialNumber',
        label: t('offboardingEquipment.serialNumber'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
          customBodyRender: (v) => v || '–',
        },
      },
      {
        name: 'status',
        label: t('offboardingEquipment.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboardingEquipment.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      dateFilterColumn('returnDueDate', t('offboardingEquipment.returnDueDate'), lang),
      dateFilterColumn('returnedAt', t('offboardingEquipment.returnedAt'), lang),
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, markReturned, confirm, waive };
};

export default useOdEquipmentApi;
